import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  validatePasswordStrength,
  generateRSAKeyPair,
  encryptPrivateKey,
  decryptPrivateKey,
  EncryptedPrivateKey,
  decryptCredential,
  encryptCredential,
  decryptNotes,
  decryptNotesUnified,
  encryptNotes,
  generateRandomBytes,
  base64ToArrayBuffer,
  DecryptedCredential
} from "../../utils/crypto/vault-crypto";
import {
  changeMasterPassword,
  resetMasterPassword,
  UpdatedVaultKeys,
  Vault
} from "../../utils/api/endpoints/vault";
import toast from "react-hot-toast";
import {
  FiShield,
  FiAlertTriangle,
  FiLock,
  FiCheckCircle,
  FiX,
  FiRefreshCw,
  FiKey,
  FiEye,
  FiEyeOff,
  FiInfo,
  FiCheck,
  FiArrowRight,
  FiZap
} from "react-icons/fi";

interface MasterPasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new password AND the server-returned updated vault key fields */
  onSuccess: (newPassword: string, updatedVault: UpdatedVaultKeys) => void;
  currentPasswordInSession?: string | null;
  vault?: Vault | null;
  allVaults?: Vault[];
}

const MasterPasswordResetModal: React.FC<MasterPasswordResetModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentPasswordInSession,
  vault,
  allVaults
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"change" | "reset">("change");

  // Form states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hard Reset specific states
  const [acceptedImpact, setAcceptedImpact] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");

  // Eagerly prefetch RSA key pair when reset tab is active so it's ready before Submit is clicked
  // generateRSAKeyPair() takes ~200-400ms; this hides the latency in the background
  const prefetchedKeyPairRef = useRef<Promise<{ privateKey: string; publicKey: string }> | null>(null);

  // Memoized password strength — only recalculates when newPassword changes, not on every render
  const validation = useMemo(
    () => (newPassword ? validatePasswordStrength(newPassword) : null),
    [newPassword]
  );

  useEffect(() => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setAcceptedImpact(false);
    setConfirmationInput("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    const initialTab = "change";
    setActiveTab(initialTab);
    prefetchedKeyPairRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Only restore scroll if we were the one who locked it
      if (isOpen) {
        document.body.style.overflow = "";
      }
    };
  }, [isOpen, onClose]);

  const handleChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword) {
      setError("Current master password is required to rotate keys.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill out all password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (newPassword === currentPassword) {
      setError("New master password must be different from your current master password.");
      return;
    }

    if (!validation?.isValid) {
      setError(validation?.feedback[0] || "New password does not meet OWASP security requirements.");
      return;
    }

    setIsLoading(true);
    try {
      let newEncryptedPrivKey = "";
      let newIv = "";
      let newSalt = "";
      let newAuthTag = "";
      let newPublicKey: string | undefined = undefined;

      const vaultsToProcess = (allVaults && allVaults.length > 0) ? allVaults : (vault ? [vault] : []);

      // 1. Verify current master password by attempting decryption of vault private key
      let privKeyDecrypted = false;
      let decryptedPrivKey = "";
      if (
        vault?.encrypted_private_key &&
        vault?.private_key_iv &&
        vault?.private_key_salt
      ) {
        try {
          decryptedPrivKey = await decryptPrivateKey(
            {
              encrypted_private_key: vault.encrypted_private_key,
              private_key_iv: vault.private_key_iv,
              private_key_salt: vault.private_key_salt,
              private_key_auth_tag: vault.private_key_auth_tag || ""
            },
            currentPassword
          );
          privKeyDecrypted = true;
        } catch (decErr) {
          console.warn("Private key decryption with currentPassword failed, testing entries:", decErr);
        }
      }

      // 2. If private key failed to decrypt, test currentPassword against symmetric entries (self-healing)
      let passwordVerified = privKeyDecrypted;
      if (!passwordVerified) {
        for (const v of vaultsToProcess) {
          if (!v.entries || v.entries.length === 0) continue;
          for (const entry of v.entries) {
            if (entry.salt && entry.encrypted_data) {
              try {
                await decryptCredential(
                  {
                    encrypted_data: entry.encrypted_data,
                    iv: entry.iv,
                    auth_tag: entry.auth_tag,
                    salt: entry.salt,
                    key_iterations: entry.key_iterations || 600000
                  },
                  currentPassword
                );
                passwordVerified = true;
                break;
              } catch {}
            }
          }
          if (passwordVerified) break;
        }
      }

      if (!passwordVerified) {
        setError(
          "Current master password is incorrect. Please check your password or use Emergency Hard Reset if lost."
        );
        setIsLoading(false);
        return;
      }

      // 3. Derive or heal the RSA private key
      if (privKeyDecrypted) {
        const reEncrypted = await encryptPrivateKey(decryptedPrivKey, newPassword);
        newEncryptedPrivKey = reEncrypted.encrypted_private_key;
        newIv = reEncrypted.private_key_iv;
        newSalt = reEncrypted.private_key_salt;
        newAuthTag = reEncrypted.private_key_auth_tag;
      } else {
        // Self-heal: Regenerate keypair and encrypt with new master password
        const keyPair = await generateRSAKeyPair();
        const reEncrypted = await encryptPrivateKey(keyPair.privateKey, newPassword);
        newEncryptedPrivKey = reEncrypted.encrypted_private_key;
        newIv = reEncrypted.private_key_iv;
        newSalt = reEncrypted.private_key_salt;
        newAuthTag = reEncrypted.private_key_auth_tag;
        newPublicKey = keyPair.publicKey;
      }

      // 4. Re-encrypt all existing symmetric credentials across all vaults using the new master password
      const reencryptedEntries: Array<{
        entry_id: number;
        encrypted_data: string;
        iv: string;
        auth_tag: string;
        salt?: string | null;
        key_iterations?: number | null;
        encrypted_notes?: string;
        notes_iv?: string;
        notes_auth_tag?: string;
      }> = [];

      for (const v of vaultsToProcess) {
        if (!v.entries || v.entries.length === 0) continue;
        for (const entry of v.entries) {
          if (entry.salt && entry.encrypted_data) {
            let decrypted: DecryptedCredential | null = null;
            try {
              // 1. Decrypt entry with current password
              decrypted = await decryptCredential(
                {
                  encrypted_data: entry.encrypted_data,
                  iv: entry.iv,
                  auth_tag: entry.auth_tag,
                  salt: entry.salt,
                  key_iterations: entry.key_iterations || 600000
                },
                currentPassword
              );
            } catch (err1) {
              // Decryption with user's entered currentPassword failed
            }

            if (!decrypted) {
              setError(
                `Unable to decrypt credential "${entry.label || `#${entry.entry_id}`}" with the provided password. Key rotation aborted to prevent data loss.`
              );
              setIsLoading(false);
              return;
            }

            // 2. Re-encrypt with new password
            const reEncrypted = await encryptCredential(decrypted, newPassword);

            // 3. Re-encrypt notes if present
            let reEncryptedNotes: { encrypted_notes: string; notes_iv: string; notes_auth_tag: string } | null = null;
            if (entry.encrypted_notes && entry.notes_iv && entry.notes_auth_tag) {
              try {
                let decryptedNotes = "";
                try {
                  decryptedNotes = await decryptNotesUnified(
                    {
                      encrypted_notes: entry.encrypted_notes,
                      notes_iv: entry.notes_iv,
                      notes_auth_tag: entry.notes_auth_tag,
                      salt: entry.salt,
                      key_iterations: entry.key_iterations,
                      encrypted_credential_key: entry.encrypted_credential_key
                    },
                    currentPassword,
                    v ? {
                      encrypted_private_key: v.encrypted_private_key,
                      private_key_iv: v.private_key_iv,
                      private_key_salt: v.private_key_salt,
                      private_key_auth_tag: v.private_key_auth_tag
                    } : undefined
                  );
                } catch {
                  if (entry.salt) {
                    decryptedNotes = await decryptNotes(
                      entry.encrypted_notes,
                      entry.notes_iv,
                      entry.notes_auth_tag,
                      currentPassword,
                      entry.salt,
                      entry.key_iterations || 600000
                    );
                  }
                }

                if (decryptedNotes) {
                  // CRITICAL: MUST use the credential entry's new salt (reEncrypted.salt), NOT a random throwaway salt!
                  // Database only stores one salt column per credential entry.
                  const newSaltArray = new Uint8Array(base64ToArrayBuffer(reEncrypted.salt));
                  const encNotes = await encryptNotes(
                    decryptedNotes,
                    newPassword,
                    newSaltArray,
                    reEncrypted.key_iterations || 600000
                  );
                  if (encNotes) {
                    reEncryptedNotes = encNotes;
                  }
                }
              } catch (notesErr) {
                console.warn(`Could not re-encrypt notes for entry ${entry.entry_id}:`, notesErr);
              }
            }

            reencryptedEntries.push({
              entry_id: entry.entry_id,
              encrypted_data: reEncrypted.encrypted_data,
              iv: reEncrypted.iv,
              auth_tag: reEncrypted.auth_tag,
              salt: reEncrypted.salt,
              key_iterations: reEncrypted.key_iterations,
              ...(reEncryptedNotes ? {
                encrypted_notes: reEncryptedNotes.encrypted_notes,
                notes_iv: reEncryptedNotes.notes_iv,
                notes_auth_tag: reEncryptedNotes.notes_auth_tag
              } : {})
            });
          }
        }
      }

      // 5. Call backend change master password API with reencrypted_entries
      const result = await changeMasterPassword({
        new_encrypted_private_key: newEncryptedPrivKey,
        private_key_iv: newIv,
        private_key_salt: newSalt,
        private_key_auth_tag: newAuthTag,
        ...(newPublicKey ? { public_key: newPublicKey } : {}),
        reencrypted_entries: reencryptedEntries
      });

      onSuccess(newPassword, result.updated_vault);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to change master password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!acceptedImpact) {
      setError("You must check the acknowledgement box before proceeding.");
      return;
    }

    if (confirmationInput.trim() !== "RESET MASTER PASSWORD") {
      setError('Confirmation text must match "RESET MASTER PASSWORD" exactly.');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please enter your new master password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (!validation?.isValid) {
      setError(validation?.feedback[0] || "New password does not meet security requirements.");
      return;
    }

    setIsLoading(true);
    try {
      // Use prefetched key pair if available, otherwise generate now (fallback)
      const keyPair = await (prefetchedKeyPairRef.current ?? generateRSAKeyPair());
      // Invalidate the cached pair so a re-open gets a fresh one
      prefetchedKeyPairRef.current = null;
      const encryptedPriv = await encryptPrivateKey(keyPair.privateKey, newPassword);

      const result = await resetMasterPassword({
        new_encrypted_private_key: encryptedPriv.encrypted_private_key,
        private_key_iv: encryptedPriv.private_key_iv,
        private_key_salt: encryptedPriv.private_key_salt,
        private_key_auth_tag: encryptedPriv.private_key_auth_tag,
        public_key: keyPair.publicKey,
        confirmation_text: "RESET MASTER PASSWORD",
        accepted_terms: true
      });

      // Note: the API wrapper (vault.ts) already fires a toast.success — no duplicate here
      onSuccess(newPassword, result.updated_vault);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to reset master password.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0f1d]/75 backdrop-blur-md p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        ref={modalRef}
        className="w-full max-w-xl rounded-[32px] bg-white shadow-2xl overflow-hidden flex flex-col border border-slate-100 my-8"
      >
        {/* Header - CTB Premium Obsidian Theme */}
        <div className="bg-[#0a0f1d] px-8 py-6 text-white flex items-center justify-between relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-40 h-40 bg-[#66d100]/10 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-[#1e293b] border border-slate-700/80 flex items-center justify-center shadow-lg shadow-black/20 text-[#66d100]">
              <FiShield className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-white tracking-tight">Master Password Security</h3>
              <p className="text-xs text-slate-400 font-medium mt-0.5">Manage or Hard Reset Zero-Knowledge Encryption Keys</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/80 rounded-xl transition-colors relative z-10"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher - CTB Branded Segmented Control */}
        <div className="bg-slate-50 border-b border-slate-100 p-2.5 px-8 flex items-center gap-2">
          <button
            onClick={() => { setActiveTab("change"); setError(null); }}
            className={`flex-1 py-3 px-4 rounded-2xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
              activeTab === "change"
                ? "bg-white text-[#0a0f1d] shadow-sm border border-slate-200/80 font-black"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FiRefreshCw className={`w-4 h-4 ${activeTab === "change" ? "text-[#66d100]" : "text-slate-400"}`} />
            Rotate Master Password
          </button>

          <button
            onClick={() => {
              setActiveTab("reset");
              setError(null);
              // Eagerly start generating RSA key pair so it's ready when user submits
              if (!prefetchedKeyPairRef.current) {
                prefetchedKeyPairRef.current = generateRSAKeyPair();
              }
            }}
            className={`flex-1 py-3 px-4 rounded-2xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
              activeTab === "reset"
                ? "bg-white text-[#0a0f1d] shadow-sm border border-slate-200/80 font-black"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FiAlertTriangle className={`w-4 h-4 ${activeTab === "reset" ? "text-slate-700" : "text-slate-400"}`} />
            Emergency Hard Reset
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-8 max-h-[75vh] overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-6 rounded-2xl bg-slate-100 border border-slate-300 p-4 text-xs font-bold text-slate-800 flex items-center gap-3 shadow-sm"
              >
                <FiAlertTriangle className="w-5 h-5 flex-shrink-0 text-slate-700" />
                <span className="leading-relaxed">{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {activeTab === "change" ? (
            <form onSubmit={handleChangeSubmit} className="space-y-6">
              {/* Informational Banner */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-2">
                <div className="flex items-center gap-2 text-slate-900 font-extrabold text-xs uppercase tracking-wide">
                  <FiShield className="w-4 h-4 text-slate-700" />
                  Zero-Knowledge Password Rotation
                </div>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  Enter your current Master Password to authenticate. Your encryption keys will be decrypted and re-encrypted with your new Master Password seamlessly, retaining all vault entries.
                </p>
              </div>

              {/* 1. Current Master Password */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-2">
                  Current Master Password <span className="text-slate-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current master password"
                    autoComplete="current-password"
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0a0f1d]/10 focus:border-[#0a0f1d] transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showCurrentPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* 2. New Master Password */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-2">
                  New Master Password <span className="text-slate-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Create a strong new master password"
                    autoComplete="new-password"
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0a0f1d]/10 focus:border-[#0a0f1d] transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showNewPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Strength Meter */}
              {validation && (
                <div className="space-y-2 bg-slate-50 border border-slate-100 p-4 rounded-2xl">
                  <div className="flex items-center justify-between text-[11px] font-bold">
                    <span className="text-slate-500 uppercase tracking-wider">Password Strength</span>
                    <span className={
                      validation.score <= 3 ? "text-slate-600" : validation.score <= 5 ? "text-slate-700" : "text-slate-900 font-extrabold"
                    }>
                      {validation.score <= 2 ? "Weak" : validation.score <= 4 ? "Fair" : validation.score <= 6 ? "Good" : "Strong"}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                          i < validation.score
                            ? "bg-[#0a0f1d]"
                            : "bg-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                  {validation.feedback.length > 0 && (
                    <ul className="text-[11px] text-slate-500 space-y-1 pt-1 font-medium">
                      {validation.feedback.map((f, idx) => (
                        <li key={idx} className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* 3. Confirm New Master Password */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-2">
                  Confirm New Master Password <span className="text-slate-400">*</span>
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new master password"
                  autoComplete="new-password"
                  className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0a0f1d]/10 focus:border-[#0a0f1d] transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3.5 rounded-2xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                  className="px-8 py-3.5 bg-[#0a0f1d] text-white rounded-2xl hover:bg-slate-800 disabled:opacity-40 transition-all font-extrabold text-xs shadow-lg shadow-slate-200 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Rotating Keys...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw className="w-4 h-4" />
                      Rotate Master Password
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetSubmit} className="space-y-6">
              {/* Consequences Warning Card - CTB Neutral Theme */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 space-y-4">
                <div className="flex items-center gap-3 text-slate-900 font-extrabold text-sm">
                  <div className="p-2 bg-slate-200/80 text-slate-700 rounded-xl">
                    <FiAlertTriangle className="w-5 h-5 flex-shrink-0" />
                  </div>
                  <span>Security & Governance Protocol</span>
                </div>
                <div className="text-xs text-slate-600 leading-relaxed space-y-3 font-medium">
                  <p className="font-bold text-slate-800">
                    What happens if the Master Password gets reset?
                  </p>
                  <ul className="space-y-2.5">
                    <li className="flex items-start gap-2.5">
                      <span className="text-slate-400 font-bold">•</span>
                      <span><strong className="text-slate-800">Zero-Knowledge Encryption Impact:</strong> Existing credentials encrypted symmetrically with the lost master password cannot be decrypted without it and will be invalidated.</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-slate-400 font-bold">•</span>
                      <span><strong className="text-slate-800">Keypair Regeneration:</strong> A fresh RSA-2048 public/private keypair will be generated and encrypted using your new Master Password.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Confirmation Checkboxes */}
              <div className="space-y-3.5 bg-slate-50 border border-slate-200/80 p-5 rounded-2xl">
                <h4 className="text-[11px] font-black uppercase text-slate-600 tracking-wider mb-3">REQUIRED CONFIRMATION & ACCEPTANCE</h4>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={acceptedImpact}
                    onChange={(e) => setAcceptedImpact(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded text-[#0a0f1d] focus:ring-[#0a0f1d] border-slate-300"
                  />
                  <span className="text-xs font-bold text-slate-700 leading-relaxed group-hover:text-slate-900">
                    I understand and accept that resetting without the old master password will invalidate existing symmetrically encrypted credentials.
                  </span>
                </label>
              </div>

              {/* Confirmation Text Entry */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-2">
                  Type <span className="font-mono text-slate-900 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-300 font-black">RESET MASTER PASSWORD</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationInput}
                  onChange={(e) => setConfirmationInput(e.target.value.toUpperCase())}
                  placeholder="RESET MASTER PASSWORD"
                  className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0a0f1d]/10 focus:border-[#0a0f1d] text-sm font-mono tracking-wider text-slate-900 font-bold placeholder:font-sans placeholder:text-slate-400"
                />
              </div>

              {/* New Password Fields */}
              <div className="space-y-5 pt-3 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-2">
                    New Master Password <span className="text-slate-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Create a strong new master password"
                      className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0a0f1d]/10 focus:border-[#0a0f1d] transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showNewPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {validation && (
                  <div className="space-y-2 bg-slate-50 border border-slate-100 p-4 rounded-2xl">
                    <div className="flex items-center justify-between text-[11px] font-bold">
                      <span className="text-slate-500 uppercase tracking-wider">Password Strength</span>
                      <span className={
                        validation.score <= 3 ? "text-slate-600" : validation.score <= 5 ? "text-slate-700" : "text-slate-900 font-extrabold"
                      }>
                        {validation.score <= 2 ? "Weak" : validation.score <= 4 ? "Fair" : validation.score <= 6 ? "Good" : "Strong"}
                      </span>
                    </div>
                    <div className="flex gap-1.5">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                            i < validation.score
                              ? "bg-[#0a0f1d]"
                              : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-2">
                    Confirm New Master Password <span className="text-slate-400">*</span>
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new master password"
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0a0f1d]/10 focus:border-[#0a0f1d] transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Reset Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3.5 rounded-2xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    !acceptedImpact ||
                    confirmationInput.trim() !== "RESET MASTER PASSWORD" ||
                    !newPassword ||
                    newPassword !== confirmPassword
                  }
                  className="px-8 py-3.5 bg-[#0a0f1d] text-white rounded-2xl hover:bg-slate-800 disabled:opacity-40 transition-all font-extrabold text-xs shadow-lg shadow-slate-200 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Executing Reset...
                    </>
                  ) : (
                    <>
                      <FiShield className="w-4 h-4" />
                      Confirm Emergency Hard Reset
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default MasterPasswordResetModal;
